import React, { useEffect, useState, useCallback, useMemo, useRef } from "react";

import A5PaymentVoucher from './paymentVoucherA5';
import Navbar from './components/navbar';
import { ErrorAlert, SuccessAlert } from './components/alertBox';
import { useLocation } from "react-router";
import { useNavigate } from "react-router-dom";
import { ChevronDown, Plus, Search, Banknote } from "lucide-react";

const EditPaymentVoucherForSuperAdmin = () => {
    const { state } = useLocation(); // Fixed: Added parentheses and use state

    const [errors, setErrors] = useState({});

    const navigate = useNavigate()
    const voucherDoc = useMemo(() => {
        return state?.voucherDoc || {};
    }, [state?.voucherDoc]);

    const [showVoucher, setShowVoucher] = useState(false);
    const [savedVoucherData, setSavedVoucherData] = useState(null);
    const [showErrors, setShowErrors] = useState(false);
    const [isOpen, setIsOpen] = useState(false);
    const debounceTimerRef = useRef(null);
    const abortControllerRef = useRef(null);
    const [filteredPayees, setFilteredPayees] = useState([]);
    const [highlightedIndex, setHighlightedIndex] = useState(-1);
    const [payableCache, setPayableCache] = useState(new Map());

    // Added formData state to manage editable fields
    const [formData, setFormData] = useState({
        date: '',
        voucherNo: '',
        payableTo: '',
        chequeDetails: '',
        poNo: '',
        grnNo: '',
        items: [{ description: '', amount: '', rs: '', cts: '' }],
        totalRs: '',
        totalCts: '',
        transferMethod: '',
    });
    const [name, setName] = useState(false)
    const inputRef = useRef(null);

    const [saveIsLoading, setSaveIsLoading] = useState(false);

    const [isTransferMethodOpen, setIsTransferMethodOpen] = useState(false);
    const transferDropdownRef = useRef(null);
    const TRANSFER_METHODS = [
        { value: 'peoples_bank', label: "People's Bank" },
        { value: 'seylan_bank', label: 'Seylan Bank' },
        { value: 'cash', label: 'Cash' }
    ];

    const fetchVoucherData = useCallback(async (id) => {
        try {
            const response = await fetch(`https://payvoucher.netgateerp.lk/api/users/payment-vouchers/${id}`);
            const resData = await response.json();
            if (!response.ok) {
                console.log("Error retrieving voucher data from backend", response.status, response.statusText);
                return;
            }
            console.log("Voucher data based on id:", resData.data);
            setFormData({
                poNo: resData.data?.poNo || '',
                grnNo: resData.data?.grnNo || '',
                date: timestampToCalenderDate(resData.data?.date) || '',
                chequeDetails: resData.data?.chequeDetails || '',
                payableTo: resData.data?.payableTo || '',
                voucherNo: resData.data?.voucherNo || '',
                transferMethod: resData.data?.transferMethod || '',
                items: resData.data?.items,
                totalRs: resData.data?.totalRs
            });
        } catch (e) {
            console.log("Error fetching voucher data", e);
        }
    }, [setFormData]); // Include setFormData as dependency

    useEffect(() => {
        // Use memoizedVoucherDoc instead of voucherDoc
        console.log("Voucher doc id:", voucherDoc);
        if (voucherDoc) {
            fetchVoucherData(voucherDoc);
        }

        return () => {
            if (debounceTimerRef.current) {
                clearTimeout(debounceTimerRef.current);
            }
            if (abortControllerRef.current) {
                abortControllerRef.current.abort();
            }
        };

    }, [voucherDoc, fetchVoucherData]);

    useEffect(() => {
        const userStr = localStorage.getItem('user')
        const userData = JSON.parse(userStr)
        const userName = userData.name
        console.log('userName', userName)
        setName(userName)
    }, [])

    const handleInputChange = async (field, value) => {
        setFormData(prev => ({ ...prev, [field]: value }));

        // Clear specific error when user starts typing
        if (errors[field]) {
            setErrors(prev => ({ ...prev, [field]: undefined }));
        }
    };

    const handleItemChange = (index, field, value) => {
        const newItems = [...formData.items];
        newItems[index] = { ...newItems[index], [field]: value };
        setFormData(prev => ({ ...prev, items: newItems }));

        // Clear item-specific errors
        if (errors.itemErrors && errors.itemErrors[`${index}_${field}`]) {
            const newItemErrors = { ...errors.itemErrors };
            delete newItemErrors[`${index}_${field}`];
            setErrors(prev => ({ ...prev, itemErrors: newItemErrors }));
        }

        // Clear general items error if an item now has content
        if (errors.items && (value.trim() || newItems[index].rs || newItems[index].cts)) {
            setErrors(prev => ({ ...prev, items: undefined }));
        }
    };

    const addItem = () => {
        setFormData(prev => ({
            ...prev,
            items: [...prev.items, { description: '', amount: '', rs: '', cts: '' }]
        }));
    };

    const removeItem = (index) => {
        if (formData.items.length > 1) {
            const newItems = formData.items.filter((_, i) => i !== index);
            setFormData(prev => ({ ...prev, items: newItems }));

            // Clean up item errors for removed item
            if (errors.itemErrors) {
                const newItemErrors = { ...errors.itemErrors };
                Object.keys(newItemErrors).forEach(key => {
                    if (key.startsWith(`${index}_`)) {
                        delete newItemErrors[key];
                    }
                    // Adjust indices for items after the removed one
                    if (key.startsWith(`${index + 1}_`)) {
                        const newKey = key.replace(`${index + 1}_`, `${index}_`);
                        newItemErrors[newKey] = newItemErrors[key];
                        delete newItemErrors[key];
                    }
                });
                setErrors(prev => ({ ...prev, itemErrors: newItemErrors }));
            }
        }
    };

    const getInputClassName = (fieldName, baseClassName) => {
        const hasError = showErrors && errors[fieldName];
        return `${baseClassName} ${hasError ? 'border-red-500 bg-red-50' : ''}`;
    };

    const getItemInputClassName = (index, field, baseClassName) => {
        const hasError = showErrors && errors.itemErrors && errors.itemErrors[`${index}_${field}`];
        return `${baseClassName} ${hasError ? 'border-red-500 bg-red-50' : ''}`;
    };
    const calculateTotal = () => {
        let totalRs = 0;
        let totalCts = 0;

        formData.items.forEach(item => {
            const rs = parseFloat(item.rs) || 0;
            const cts = parseInt(item.cts) || 0;

            totalRs += rs;
            totalCts += cts;
        });

        // Convert cents overflow to rupees
        totalRs += Math.floor(totalCts / 100);
        // totalCts = totalCts % 100;

        return { rs: totalRs, };
    };

    const { rs: totalRs } = calculateTotal();

    const handlePayeeSelect = (payee) => {
        console.log("Selected payee:", payee);
        setFormData(prev => ({ ...prev, payableTo: payee }));
        setIsOpen(false);
        setHighlightedIndex(-1);
        inputRef.current?.blur();
    };

    const searchPayableNames = useCallback(async (searchTerm) => {
        // Clear any pending requests
        if (abortControllerRef.current) {
            abortControllerRef.current.abort();
        }

        const trimmedSearch = searchTerm;

        if (!trimmedSearch) {
            setFilteredPayees([]);
            setIsOpen(false);
            return;
        }


        // 3. No cache hit - fetch from server
        console.log("Fetching from server for:", trimmedSearch);

        try {
            // Create new abort controller for this request
            abortControllerRef.current = new AbortController();

            const response = await fetch(
                `https://payvoucher.netgateerp.lk/api/users/getPayableName/${encodeURIComponent(searchTerm)}`,
                { signal: abortControllerRef.current.signal }
            );

            const data = await response.json();
            console.log("Payable name data:", data);

            if (!response.ok) {

                setFilteredPayees([]);
                setIsOpen(false);
                return;
            }

            const payableNames = Array.isArray(data)
                ? data.map(item => item.payableName)
                : [];

            console.log("Fetched payable names:", payableNames);

            // Cache the results
            setPayableCache(prev => new Map(prev).set(trimmedSearch, payableNames));

            // Limit cache size to prevent memory issues
            if (payableCache.size > 50) {
                const firstKey = payableCache.keys().next().value;
                setPayableCache(prev => {
                    const newCache = new Map(prev);
                    newCache.delete(firstKey);
                    return newCache;
                });
            }


            setFilteredPayees(payableNames);
            setIsOpen(true);
            setHighlightedIndex(-1);

        } catch (e) {
            if (e.name === 'AbortError') {
                console.log("Request aborted");
                return;
            }
            console.error("Error fetching payable names:", e);

            setFilteredPayees([]);
            setIsOpen(false);
        }
    }, [payableCache]);

    const handlePayableInput = useCallback((field, value) => {
        setFormData(prev => ({ ...prev, [field]: value }));
        if (field === 'payableTo') {
            // Clear existing timer
            if (debounceTimerRef.current) {
                clearTimeout(debounceTimerRef.current);
            }

            // For empty input, clear immediately without debounce
            if (!value.trim()) {
                setFilteredPayees([]);
                setIsOpen(false);
                return;
            }

            // Set new timer (300ms delay - adjust as needed)
            debounceTimerRef.current = setTimeout(() => {
                searchPayableNames(value);
            }, 300);
        } else {
            setFilteredPayees([]);
            setIsOpen(false);
        }
    }, [searchPayableNames]);

    const handleKeyDown = (e) => {
        if (!isOpen) return;

        switch (e.key) {
            case 'ArrowDown':
                e.preventDefault();
                setHighlightedIndex(prev =>
                    prev < filteredPayees.length - 1 ? prev + 1 : prev
                );
                break;
            case 'ArrowUp':
                e.preventDefault();
                setHighlightedIndex(prev => prev > 0 ? prev - 1 : -1);
                break;
            case 'Enter':
                e.preventDefault();
                if (highlightedIndex >= 0) {
                    handlePayeeSelect(filteredPayees[highlightedIndex]);
                } else if (formData.payableTo.trim()) {
                    // Add new payee
                    setIsOpen(false);
                }
                break;
            case 'Escape':
                setIsOpen(false);
                setHighlightedIndex(-1);
                break;
            default:
                setIsOpen(false);
        }
    };



    // Added missing handleSave function
    const handleSave = async () => {
        setSaveIsLoading(true);

        try {
            const updatedVoucherData = {
                date: formData.date,
                payableTo: formData.payableTo,
                poNo: formData.poNo,
                grnNo: formData.grnNo,
                chequeDetails: formData.chequeDetails,
                transferMethod: formData.transferMethod,
                items: formData.items,
                totalRs: totalRs,
                name: name,
                totalCts: 0 // Since you removed totalCts calculation
            };
            console.log("Updated voucher data:", updatedVoucherData)

            const response = await fetch(`https://payvoucher.netgateerp.lk/api/users/update-full-payment-voucher/${voucherDoc}`, {
                method: 'PUT',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify(updatedVoucherData)
            })

            if (!response.ok) {
                console.log("Error in updating the data", response.status, response.statusText)
                ErrorAlert("Error in updating the data")
                return
            }

            SuccessAlert("Successfully updated the payment voucher")
            setSavedVoucherData({ ...formData, totalRs: totalRs })
            setShowVoucher(true)
            setSaveIsLoading(false)
        } catch (error) {
            console.error('Save error:', error);
            setSaveIsLoading(false)
        } finally {
            setSaveIsLoading(false);
        }
    };

    // Added clear function for editable fields only
    const clearEditableFields = () => {
        setFormData({
            date: '',
            voucherNo: '',
            payableTo: '',
            chequeDetails: '',
            transferMethod: '',
            poNo: '',
            grnNo: '',
            items: [{ description: '', amount: '', rs: '', cts: '' }],
            // preparedBy: '',
            // authorisedBy: '',
            // receiverSignature: ''
        });
        setErrors({});
        setShowErrors(false);
    };

    const timestampToCalenderDate = (timestamp) => {
        const date = new Date(timestamp);
        const day = String(date.getDate()).padStart(2, '0');
        const month = String(date.getMonth() + 1).padStart(2, '0');
        const year = date.getFullYear();

        return `${year}-${month}-${day}`;;
    };


    return (
        <>
            <div className="w-full min-h-screen bg-gray-50 flex flex-col overflow-x-hidden">
                <Navbar />
                <div className="flex-1 p-4 overflow-x-hidden">
                    <div className="border-2 border-blue-800 min-h-full bg-white shadow-lg max-w-full">
                        {/* Header */}
                        <div className="border-b-2 border-blue-800 p-4 sm:p-8">
                            <div className="flex flex-col sm:flex-row items-start">
                                <div className="w-24 h-24 sm:w-32 sm:h-32 flex items-center justify-center mb-4 sm:mb-0 sm:mr-8 mx-auto sm:mx-0">
                                    <img
                                        src="/netgatelogo.png"
                                        alt="NetGate Lanka Logo"
                                        className="w-full h-full object-contain"
                                    />
                                </div>
                                <div className="text-blue-800 text-center flex-1 min-w-0">
                                    <div className="font-bold text-xl sm:text-3xl mb-3 leading-tight">NetGate Lanka (Pvt) Ltd.</div>
                                    <div className="text-sm sm:text-lg mb-2 leading-relaxed">No. 251/1A, Moratuwa Road, Piliyandala.</div>
                                    <div className="text-sm sm:text-lg mb-6 leading-relaxed">Tel: 070 166 6777</div>
                                    <div className="border-2 border-blue-800 inline-block px-4 sm:px-8 py-3 mt-2">
                                        <div className="font-semibold text-lg sm:text-xl">PAYMENT VOUCHER</div>
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* Form Fields */}
                        <div className="p-4 sm:p-8 overflow-x-hidden">
                            {/* Top row  */}
                            <div className="flex flex-col sm:flex-row sm:justify-between mb-8 gap-4">
                                <div className="flex items-center">
                                    <span className="text-sm mr-4 text-blue-800 font-medium min-w-[50px]">Date :</span>
                                    <input
                                        type="date"
                                        value={formData.date}
                                        onChange={(e) => handleInputChange('date', e.target.value)}
                                        className={getInputClassName('date', 'border-b-2 border-dotted border-blue-600 focus:border-blue-600 outline-none px-2 py-2 w-36 sm:w-44 text-sm')}
                                    />
                                    {showErrors && errors.date && (
                                        <span className="text-red-500 text-xs ml-3">{errors.date}</span>
                                    )}
                                </div>
                                <div className="flex items-center">
                                    <span className="text-sm mr-4 text-blue-800 font-medium">No.</span>
                                    <input
                                        type="text"
                                        value={formData.voucherNo || ''}
                                        readOnly
                                        className="border-b-2 border-dotted border-gray-400 bg-gray-100 cursor-not-allowed px-2 py-2 w-24 font-bold text-sm text-gray-600"
                                        title="Auto-generated voucher number (read-only)"
                                    />
                                </div>
                            </div>

                            {/* Second row  */}
                            <div className="mb-8">
                                <div className="flex items-center relative">
                                    <span className="text-sm mr-4 text-blue-800 font-medium min-w-[100px]">Payable to :</span>
                                    {/* Wrapper div for input and dropdown icon */}
                                    <div className="flex-1 ml-3 relative">
                                        <input
                                            type="text"
                                            ref={inputRef}
                                            value={formData.payableTo}
                                            onChange={(e) => handlePayableInput('payableTo', e.target.value)}
                                            onKeyDown={handleKeyDown}
                                            onFocus={() => {
                                                if (formData.payableTo) {
                                                    setIsOpen(true);
                                                }
                                            }}
                                            className={getInputClassName('payableTo', 'border-b-2 border-dotted border-blue-600 focus:border-blue-600 outline-none px-2 py-2 w-full text-sm min-w-0 pr-8')}
                                            placeholder="Enter payee name"
                                            autoComplete="off"
                                        />
                                        {/* Dropdown icon - positioned relative to the input wrapper */}
                                        <ChevronDown
                                            className={`absolute right-2 top-1/2 transform -translate-y-1/2 w-4 h-4 text-blue-600 cursor-pointer transition-transform ${isOpen ? 'rotate-180' : ''}`}
                                            onClick={() => {
                                                setIsOpen(!isOpen);
                                                if (!isOpen) {
                                                    inputRef.current?.focus();
                                                }
                                            }}
                                        />

                                        {/* Dropdown menu - positioned absolute relative to the input wrapper */}
                                        {isOpen && (
                                            <div
                                                className="absolute z-50 w-full mt-1 bg-white border border-gray-200 rounded-md shadow-lg max-h-60 overflow-y-auto"
                                                style={{
                                                    top: '100%',
                                                    left: 0,
                                                    right: 0,
                                                    minWidth: '100%'
                                                }}
                                            >
                                                {filteredPayees.length > 0 ? (
                                                    <>
                                                        {filteredPayees.map((payee, index) => (
                                                            <div
                                                                key={payee}
                                                                className={`px-3 py-2 cursor-pointer text-sm flex items-center hover:bg-blue-50 ${index === highlightedIndex ? 'bg-blue-50' : ''}`}
                                                                onClick={(e) => {
                                                                    e.preventDefault();
                                                                    e.stopPropagation();
                                                                    handlePayeeSelect(payee);
                                                                }}
                                                                onMouseDown={(e) => {
                                                                    e.preventDefault(); // Prevent input blur
                                                                }}
                                                                onMouseEnter={() => setHighlightedIndex(index)}
                                                            >
                                                                <Search className="w-3 h-3 text-gray-400 mr-2" />
                                                                <span className="truncate">{payee}</span>
                                                            </div>
                                                        ))}
                                                    </>
                                                ) : formData.payableTo.trim() ? (
                                                    <div className="px-3 py-2 text-sm text-gray-600 flex items-center"
                                                        onClick={(e) => {
                                                            e.preventDefault();
                                                            e.stopPropagation();
                                                            handlePayeeSelect(formData.payableTo);
                                                        }}
                                                        onMouseDown={(e) => {
                                                            e.preventDefault(); // Prevent input blur
                                                        }}>
                                                        <Plus className="w-3 h-3 text-green-500 mr-2" />
                                                        <span> New payee: "{formData.payableTo}"</span>
                                                    </div>
                                                ) : (
                                                    <div className="px-3 py-2 text-sm text-gray-500">
                                                        Start typing to search payees...
                                                    </div>
                                                )}
                                            </div>
                                        )}
                                    </div>
                                </div>
                                {/* Your existing error display */}
                                {showErrors && errors.payableTo && (
                                    <div className="text-red-500 text-xs mt-2 ml-28">{errors.payableTo}</div>
                                )}
                            </div>

                            {/* Third row  */}
                            <div className="mb-8">
                                <div className="flex items-center relative" ref={transferDropdownRef}>
                                    <span className="text-sm mr-4 text-blue-800 font-medium min-w-[140px] sm:min-w-[180px]">Transfer Method :</span>
                                    <div className="flex-1 ml-3 relative">
                                        <div
                                            className={getInputClassName('transferMethod', `border-b-2 border-dotted border-blue-600 focus:border-blue-600 px-2 py-2 w-full text-sm min-w-0 pr-8 bg-white cursor-pointer flex items-center ${formData.transferMethod ? 'text-black' : 'text-gray-500'}`)}
                                            onClick={() => setIsTransferMethodOpen(!isTransferMethodOpen)}
                                        >
                                            {formData.transferMethod ? (
                                                <div className="flex items-center">
                                                    {formData.transferMethod === "peoples_bank" && (
                                                        <img src="/peoples_bank.png" alt="People's Bank" className="w-5 h-5 mr-2 object-contain" />
                                                    )}
                                                    {formData.transferMethod === "seylan_bank" && (
                                                        <img src="/seylan_bank.png" alt="Seylan Bank" className="w-5 h-5 mr-2 object-contain" />
                                                    )}
                                                    {formData.transferMethod === "cash" && (
                                                        <Banknote className="w-5 h-5 mr-2 text-green-600" />
                                                    )}
                                                    <span>
                                                        {TRANSFER_METHODS.find(m => m.value === formData.transferMethod)?.label || formData.transferMethod}
                                                    </span>
                                                </div>
                                            ) : (
                                                "Select transfer method"
                                            )}
                                        </div>
                                        <div
                                            className="absolute right-2 top-1/2 transform -translate-y-1/2 cursor-pointer"
                                            onClick={() => setIsTransferMethodOpen(!isTransferMethodOpen)}
                                        >
                                            <ChevronDown className={`w-4 h-4 text-blue-600 transition-transform ${isTransferMethodOpen ? 'rotate-180' : ''}`} />
                                        </div>

                                        {isTransferMethodOpen && (
                                            <div
                                                className="absolute z-50 w-full mt-1 bg-white border border-gray-200 rounded-md shadow-lg max-h-60 overflow-y-auto"
                                                style={{
                                                    top: '100%',
                                                    left: 0,
                                                    right: 0,
                                                    minWidth: '100%'
                                                }}
                                            >
                                                {TRANSFER_METHODS.map((method) => (
                                                    <div
                                                        key={method.value}
                                                        className="px-3 py-2 cursor-pointer text-sm flex items-center hover:bg-blue-50"
                                                        onClick={() => {
                                                            handleInputChange('transferMethod', method.value);
                                                            setIsTransferMethodOpen(false);
                                                        }}
                                                    >
                                                        {method.value === "peoples_bank" && (
                                                            <img src="/peoples_bank.png" alt="People's Bank" className="w-5 h-5 mr-3 object-contain" />
                                                        )}
                                                        {method.value === "seylan_bank" && (
                                                            <img src="/seylan_bank.png" alt="Seylan Bank" className="w-5 h-5 mr-3 object-contain" />
                                                        )}
                                                        {method.value === "cash" && (
                                                            <Banknote className="w-5 h-5 mr-3 text-green-600" />
                                                        )}
                                                        <span>{method.label}</span>
                                                    </div>
                                                ))}
                                            </div>
                                        )}
                                    </div>
                                </div>
                                {showErrors && errors.transferMethod && (
                                    <div className="text-red-500 text-xs mt-2 ml-36 sm:ml-48">{errors.transferMethod}</div>
                                )}
                            </div>

                            <div className="mb-8">
                                <div className="flex items-center">
                                    <span className="text-sm mr-4 text-blue-800 font-medium min-w-[140px] sm:min-w-[180px]">Cheque No./Cash/Online :</span>
                                    <input
                                        type="text"
                                        value={formData.chequeDetails || ''}
                                        onChange={(e) => handleInputChange('chequeDetails', e.target.value)}
                                        className="border-0 border-b-2 border-dotted border-blue-600 outline-none focus:outline-none px-2 py-2 flex-1 ml-3 text-sm min-w-0 text-gray-900"
                                        placeholder="Enter Cheque No./Cash/Online details"
                                        title="This field is editable"
                                    />
                                </div>
                            </div>

                            {/* Fourth row - EDITABLE PO and GRN fields */}
                            <div className="flex flex-col sm:flex-row sm:justify-between mb-10 gap-4">
                                <div className="flex items-center">
                                    <span className="text-sm mr-4 text-blue-800 font-medium">P.O. No.</span>
                                    <input
                                        type="text"
                                        value={formData.poNo}
                                        onChange={(e) => handleInputChange('poNo', e.target.value)}
                                        className="border-b-2 border-dotted border-blue-600 focus:border-blue-600 outline-none px-2 py-2 w-32 sm:w-36 ml-3 text-sm"
                                        placeholder="P.O. Number"
                                        title="This field is editable"
                                    />
                                </div>
                                <div className="flex items-center">
                                    <span className="text-sm mr-4 text-blue-800 font-medium">G.R.N. No.</span>
                                    <input
                                        type="text"
                                        value={formData.grnNo}
                                        onChange={(e) => handleInputChange('grnNo', e.target.value)}
                                        className="border-b-2 border-dotted border-blue-600 focus:border-blue-600 outline-none px-2 py-2 w-32 sm:w-36 ml-3 text-sm "
                                        placeholder="G.R.N. Number"
                                        title="This field is editable"
                                    />
                                </div>
                            </div>

                            {/* Table  */}
                            <div className="border-2 border-blue-800 mb-10 shadow-sm overflow-x-auto">
                                <div className="min-w-full">
                                    {/* Table header */}
                                    <div className="text-center text-sm font-semibold bg-blue-50">
                                        <div className="flex border-b-2 border-blue-800 min-w-[500px]">
                                            <div className="flex-1 border-r-2 border-blue-800 py-4 min-w-[300px]">
                                                Description
                                            </div>
                                            <div className="w-48 sm:w-64">
                                                <div className="py-2 border-b border-blue-800">
                                                    Amount
                                                </div>
                                                <div className="flex">
                                                    <div className="flex-1 py-2 border-r-2 border-blue-800">
                                                        Rs
                                                    </div>
                                                    <div className="flex-1 py-2">
                                                        Cts
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    </div>

                                    {/* Table content */}
                                    <div className="min-h-48 flex flex-col min-w-[500px]">
                                        {/* Data rows */}
                                        {formData.items.map((item, index) => (
                                            <div key={index} className="flex border-b border-blue-800 hover:bg-gray-50">
                                                <div className="flex-1 border-r-2 border-blue-800 p-4 min-w-[300px]">
                                                    <textarea
                                                        value={item.description}
                                                        onChange={(e) => handleItemChange(index, 'description', e.target.value)}
                                                        className={getItemInputClassName(index, 'description', 'w-full outline-none  bg-transparent text-sm resize-none')}
                                                        placeholder="Item description"
                                                        rows={2}
                                                        style={{ minHeight: '40px' }}
                                                    />
                                                    {showErrors && errors.itemErrors && errors.itemErrors[`${index}_description`] && (
                                                        <div className="text-red-500 text-xs mt-2">{errors.itemErrors[`${index}_description`]}</div>
                                                    )}
                                                </div>
                                                <div className="w-48 sm:w-64 flex">
                                                    <div className="flex-1 border-r-2 border-blue-800 p-4">
                                                        <input
                                                            type="number"
                                                            value={item.rs}
                                                            onChange={(e) => handleItemChange(index, 'rs', e.target.value)}
                                                            className={getItemInputClassName(index, 'rs', 'w-full outline-none py-2 text-center bg-transparent text-sm')}
                                                            placeholder="0"
                                                            step="0.01"
                                                            min="0"
                                                        />
                                                        {showErrors && errors.itemErrors && errors.itemErrors[`${index}_rs`] && (
                                                            <div className="text-red-500 text-xs mt-2 text-center">{errors.itemErrors[`${index}_rs`]}</div>
                                                        )}
                                                    </div>
                                                    <div className="flex-1 p-4 flex items-center">
                                                        <input
                                                            type="number"
                                                            value={item.cts}
                                                            onChange={(e) => handleItemChange(index, 'cts', e.target.value)}
                                                            className={getItemInputClassName(index, 'cts', 'w-full outline-none py-2 text-center bg-transparent text-sm')}
                                                            placeholder="0"
                                                            min="0"
                                                            max="99"
                                                        />
                                                        {formData.items.length > 1 && (
                                                            <button
                                                                onClick={() => removeItem(index)}
                                                                className="ml-3 text-red-500 hover:text-red-700 text-lg font-bold w-6 h-6 flex items-center justify-center rounded-full hover:bg-red-100 flex-shrink-0"
                                                                title="Remove item"
                                                            >
                                                                ×
                                                            </button>
                                                        )}
                                                    </div>
                                                    {showErrors && errors.itemErrors && errors.itemErrors[`${index}_cts`] && (
                                                        <div className="text-red-500 text-xs mt-2 text-center absolute">{errors.itemErrors[`${index}_cts`]}</div>
                                                    )}
                                                </div>
                                            </div>
                                        ))}

                                        {/* Add item row */}
                                        <div className="flex border-b border-blue-800 bg-blue-25">
                                            <div className="flex-1 border-r-2 border-blue-800 p-4 min-w-[300px]">

                                                <button
                                                    onClick={addItem}
                                                    className="text-blue-600 hover:text-blue-800 text-sm font-medium px-3 py-2 rounded hover:bg-blue-50 transition-colors"
                                                >
                                                    + Add Item
                                                </button>


                                            </div>
                                            <div className="w-48 sm:w-64 flex">
                                                <div className="flex-1 border-r-2 border-blue-800 p-4"></div>
                                                <div className="flex-1 p-4"></div>
                                            </div>
                                        </div>

                                        {/* Empty space filler */}
                                        <div className="flex flex-1">
                                            <div className="flex-1 border-r-2 border-blue-800 min-w-[300px]"></div>
                                            <div className="w-48 sm:w-64 flex">
                                                <div className="flex-1 border-r-2 border-blue-800"></div>
                                                <div className="flex-1"></div>
                                            </div>
                                        </div>
                                    </div>

                                    {/* Total row */}
                                    <div className="border-t-2 border-blue-800 text-center text-sm font-bold bg-blue-100 min-w-[500px]">
                                        <div className="flex">
                                            <div className="flex-1 border-r-2 border-blue-800 py-4 text-blue-800 min-w-[300px]">
                                                Total
                                            </div>
                                            <div className="w-48 sm:w-64 flex text-blue-800">
                                                <div className="flex-1 border-r-2 border-blue-800 py-4">
                                                    {totalRs.toFixed(2)}
                                                </div>
                                                <div className="flex-1 py-4">
                                                    {/* {totalCts.toString().padStart(2, '0')} */}
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                            {/* Total amount error */}
                            {showErrors && errors.totalAmount && (
                                <div className="text-red-500 text-sm mb-6 p-3 bg-red-50 border-l-4 border-red-500">{errors.totalAmount}</div>
                            )}


                            {/* Action buttons */}
                            <div className="flex flex-col sm:flex-row justify-end gap-4 sm:gap-6 mt-12 pt-6">
                                <button
                                    onClick={handleSave}
                                    disabled={saveIsLoading}
                                    className="px-8 py-3 bg-[#1B2F69] text-white rounded-lg hover:bg-blue-800 transition-colors font-medium shadow-md disabled:opacity-50 disabled:cursor-not-allowed"
                                >
                                    {saveIsLoading ? 'Saving...' : 'Save Changes'}
                                </button>
                                <button
                                    onClick={clearEditableFields}
                                    className="px-8 py-3 bg-gray-500 text-white rounded-lg hover:bg-gray-600 transition-colors font-medium shadow-md"
                                >
                                    Clear Editable Fields
                                </button>
                            </div>


                        </div>
                    </div>
                </div>
            </div>

            {/* Show ViewPaymentVoucher component when saved */}
            {showVoucher && savedVoucherData && (
                <A5PaymentVoucher
                    voucherData={savedVoucherData}
                    onClose={() => {
                        setShowVoucher(false);
                        navigate('/home')
                    }}
                />
            )}
        </>
    )
}

export default EditPaymentVoucherForSuperAdmin;